/*
 * Trail guide article (sections/dte-trail-guide.liquid). Plain JavaScript, no library, deferred.
 * Same logic as the approved prototype (docs/design-directions/cape-to-cape-pilot.template.html):
 * the track line draws with scroll, day-end X marks light up, the walker dot moves; on phones a
 * bottom trail bar shows the same progress. Scroll work is batched into one requestAnimationFrame.
 * Stops mode (cfg.mode 'stops', day trips and town guides) counts stops instead of km: see initStops.
 * Also: the plan toggle, the packing list (ticks saved in localStorage) and the water calculator.
 * Everything it reads comes from the page's own markup and a JSON block written by Liquid.
 * Without it the page still reads top to bottom: every day, the full list, the default water sum.
 */
(function () {
  'use strict';

  function all(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  function initTrail(root, cfg) {
    var days = root.querySelector('[data-tg-days]');
    if (!days) return function () {};
    var TOTAL = cfg.total || 1;
    var ENDS = cfg.ends || [];
    var NAMES = cfg.names || [];
    var track = root.querySelector('[data-tg-track]');
    var walker = root.querySelector('[data-tg-walker]');
    var len = 0;
    if (track && typeof track.getTotalLength === 'function') {
      len = track.getTotalLength();
      track.style.strokeDasharray = len;
      track.style.strokeDashoffset = len;
    }
    var xs = all(root, '[data-tg-x]');
    var labels = all(root, '[data-tg-label]');
    var bar = root.querySelector('[data-tg-bar]');
    var fill = root.querySelector('[data-tg-tb-fill]');
    var line = root.querySelector('[data-tg-tb-line]');
    ENDS.forEach(function (k) {
      var s = document.createElement('span');
      s.className = 'dte-tg__bar-x';
      s.textContent = '×';
      s.style.left = (k / TOTAL) * 100 + '%';
      line.appendChild(s);
    });
    var tbx = all(line, '.dte-tg__bar-x');
    var rdKm = root.querySelector('[data-tg-rd-km]');
    var rdDay = root.querySelector('[data-tg-rd-day]');
    var tbKm = root.querySelector('[data-tg-tb-km]');
    var tbDay = root.querySelector('[data-tg-tb-day]');
    var last = NAMES[NAMES.length - 1] || '';
    var ticking = false;

    function update() {
      ticking = false;
      if (days.offsetParent === null) {
        bar.classList.remove('is-on');
        return;
      }
      var r = days.getBoundingClientRect();
      var vh = window.innerHeight;
      var p = (vh * 0.55 - r.top) / r.height;
      p = Math.max(0, Math.min(1, p));
      var km = Math.round(p * TOTAL);
      if (track && len) {
        track.style.strokeDashoffset = len * (1 - p);
        var pt = track.getPointAtLength(len * p);
        walker.setAttribute('cx', pt.x);
        walker.setAttribute('cy', pt.y);
      }
      var d = 0;
      while (d < ENDS.length && km >= ENDS[d]) d++;
      function mark(el, i) {
        el.classList.toggle('reached', km >= ENDS[i]);
      }
      xs.forEach(mark);
      labels.forEach(mark);
      tbx.forEach(mark);
      var done = d >= ENDS.length;
      rdKm.textContent = km;
      tbKm.textContent = km;
      rdDay.textContent = done ? last : cfg.day + ' ' + (d + 1);
      tbDay.textContent = done
        ? cfg.finished + ' · ' + last
        : cfg.day + ' ' + (d + 1) + ' · ' + NAMES[d] + ' ' + cfg.to + ' ' + NAMES[d + 1];
      fill.style.transform = 'scaleX(' + p + ')';
      bar.classList.toggle('is-on', r.top < vh * 0.6 && r.bottom > vh * 0.4);
    }

    function onScroll() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    }
    // Horizon scrolls .page-wrapper, not the window, on wide screens. Scroll events do not bubble,
    // so listen in the capture phase on the document: that hears whichever element scrolls.
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onScroll);
    update();
    return update;
  }

  // Stops mode (day trips, town and rainy-day guides): the same line, dot and phone bar, counted in
  // stops instead of kilometres. Same stop logic as the gift guide's run (dte-gift-guide.js): scroll
  // progress spreads evenly over the stops, and a stop's X lights once the reader is a quarter of
  // the way to it. The dot's path is split at each stop's waypoint, so it arrives on the X exactly.
  function initStops(root, cfg) {
    var days = root.querySelector('[data-tg-days]');
    if (!days) return function () {};
    var NAMES = cfg.stops || [];
    var N = NAMES.length;
    var track = root.querySelector('[data-tg-track]');
    var walker = root.querySelector('[data-tg-walker]');
    var len = 0;
    if (track && typeof track.getTotalLength === 'function') {
      len = track.getTotalLength();
      track.style.strokeDasharray = len;
      track.style.strokeDashoffset = len;
    }
    // Length along the line at each stop: sample the path once and take the nearest sample to
    // each stop's waypoint, moving forward only. A stop with no waypoint (location not confirmed,
    // so no pin) sits evenly between its neighbours that have one; with no waypoints at all the
    // stops are spaced evenly along the whole line.
    var at = [];
    var pts = cfg.points || [];
    var known = len > 0 && pts.length === N && pts.some(function (p) { return p; });
    if (known) {
      var S = 400;
      var samples = [];
      for (var s = 0; s <= S; s++) samples.push(track.getPointAtLength((len * s) / S));
      var from = 0;
      pts.forEach(function (p) {
        if (!p) {
          at.push(null);
          return;
        }
        var best = from;
        var bd = Infinity;
        for (var j = from; j <= S; j++) {
          var dx = samples[j].x - p[0];
          var dy = samples[j].y - p[1];
          var dd = dx * dx + dy * dy;
          if (dd < bd) {
            bd = dd;
            best = j;
          }
        }
        from = best;
        at.push((len * best) / S);
      });
      for (var g = 0; g < N; g++) {
        if (at[g] !== null) continue;
        var e = g;
        while (e < N && at[e] === null) e++;
        var lo = g > 0 ? at[g - 1] : 0;
        var hi = e < N ? at[e] : len;
        for (var f = g; f < e; f++) at[f] = lo + ((hi - lo) * (f - g + 1)) / (e - g + 1);
        g = e;
      }
    } else {
      for (var k = 0; k < N; k++) at.push(N > 1 ? (len * k) / (N - 1) : 0);
    }
    // Anchors along the scroll: the stops, plus the start when the first stop is further on.
    var lead = cfg.atStart || !at.length || at[0] < 1 ? 0 : 1;
    var anchors = lead ? [0].concat(at) : at.slice();
    var SEG = Math.max(1, anchors.length - 1);
    var xs = all(root, '[data-tg-x]');
    var labels = all(root, '[data-tg-label]');
    var bar = root.querySelector('[data-tg-bar]');
    var fill = root.querySelector('[data-tg-tb-fill]');
    var line = root.querySelector('[data-tg-tb-line]');
    anchors.forEach(function (a, i) {
      if (i < lead) return;
      var el = document.createElement('span');
      el.className = 'dte-tg__bar-x';
      el.textContent = '×';
      el.setAttribute('data-tg-i', i - lead);
      el.style.left = (i / SEG) * 100 + '%';
      line.appendChild(el);
    });
    var tbx = all(line, '.dte-tg__bar-x');
    var rdCount = root.querySelector('[data-tg-rd-km]').parentNode;
    var rdName = root.querySelector('[data-tg-rd-day]');
    var tbCount = root.querySelector('[data-tg-tb-km]').parentNode;
    var tbName = root.querySelector('[data-tg-tb-day]');
    // "Stop 3 of 5", or "Start" before the first stop when the route starts somewhere else.
    function count(span, i) {
      span.textContent = '';
      if (i < 0) {
        span.textContent = cfg.startLabel;
        return;
      }
      var b = document.createElement('b');
      b.textContent = i + 1;
      span.appendChild(document.createTextNode(cfg.stop + ' '));
      span.appendChild(b);
      span.appendChild(document.createTextNode(' ' + cfg.of + ' ' + N));
    }
    var cards = Array.prototype.filter.call(days.children, function (el) { return el.classList.contains('dte-tg__day'); });
    var ticking = false;
    var lastI = null;

    function update() {
      ticking = false;
      if (days.offsetParent === null) {
        bar.classList.remove('is-on');
        return;
      }
      var r = days.getBoundingClientRect();
      var vh = window.innerHeight;
      var p = (vh * 0.55 - r.top) / r.height;
      p = Math.max(0, Math.min(1, p));
      var q = p * SEG;
      if (track && len) {
        var a = Math.min(SEG - 1, Math.floor(q));
        var l = anchors.length > 1 ? anchors[a] + (anchors[a + 1] - anchors[a]) * (q - a) : len * p;
        track.style.strokeDashoffset = len - l;
        var pt = track.getPointAtLength(l);
        walker.setAttribute('cx', pt.x);
        walker.setAttribute('cy', pt.y);
      }
      // The stop named in the readout and the phone bar is the stop card being read: the last card
      // whose top has passed the same reading line, so "Stop 2 of 3" always matches the card's "STOP 2".
      // Before the first card: "Start" when the route starts elsewhere, else stop 1.
      var i = lead ? -1 : 0;
      for (var c = 0; c < cards.length; c++) if (cards[c].getBoundingClientRect().top <= vh * 0.55) i = c;
      i = Math.min(N - 1, i);
      if (i !== lastI) {
        lastI = i;
        function mark(el) {
          el.classList.toggle('reached', +el.getAttribute('data-tg-i') <= i);
        }
        xs.forEach(mark);
        labels.forEach(mark);
        tbx.forEach(mark);
        rdName.textContent = i < 0 ? cfg.start : NAMES[i];
        tbName.textContent = i < 0 ? cfg.start : NAMES[i];
        count(rdCount, i);
        count(tbCount, i);
      }
      fill.style.transform = 'scaleX(' + p + ')';
      bar.classList.toggle('is-on', r.top < vh * 0.6 && r.bottom > vh * 0.4);
    }

    function onScroll() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    }
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onScroll);
    update();
    return update;
  }

  function initToggle(root, update) {
    var toggle = root.querySelector('[data-tg-toggle]');
    var grid = root.querySelector('[data-tg-grid]');
    if (!toggle || !grid) return;
    toggle.hidden = false;
    var buttons = all(toggle, 'button');
    buttons.forEach(function (b) {
      b.addEventListener('click', function () {
        buttons.forEach(function (o) {
          o.setAttribute('aria-pressed', o === b ? 'true' : 'false');
        });
        grid.classList.toggle('is-short', b.getAttribute('data-plan') === 'short');
        update();
      });
    });
  }

  function initPack(root) {
    var pack = root.querySelector('[data-tg-pack]');
    if (!pack) return;
    var KEY = pack.getAttribute('data-key');
    var saved = {};
    try {
      saved = JSON.parse(localStorage.getItem(KEY) || '{}') || {};
    } catch (e) {
      saved = {};
    }
    var boxes = all(pack, 'input[type=checkbox]');
    var progress = pack.querySelector('[data-tg-progress]');
    var pbar = pack.querySelector('[data-tg-pbar]');
    var pc = pack.querySelector('[data-tg-pcount]');
    var reset = pack.querySelector('[data-tg-reset]');
    progress.hidden = false;
    reset.hidden = false;
    function tally() {
      var n = boxes.filter(function (b) {
        return b.checked;
      }).length;
      pc.textContent = n + ' ' + pc.getAttribute('data-of') + ' ' + boxes.length + ' ' + pc.getAttribute('data-packed');
      pbar.style.transform = 'scaleX(' + (boxes.length ? n / boxes.length : 0) + ')';
    }
    function save() {
      try {
        localStorage.setItem(KEY, JSON.stringify(saved));
      } catch (e) {
        /* private mode: ticks just aren't kept */
      }
    }
    boxes.forEach(function (b) {
      b.checked = !!saved[b.id];
      b.addEventListener('change', function () {
        saved[b.id] = b.checked;
        save();
        tally();
      });
    });
    reset.addEventListener('click', function () {
      boxes.forEach(function (b) {
        b.checked = false;
      });
      saved = {};
      try {
        localStorage.removeItem(KEY);
      } catch (e) {
        /* same */
      }
      tally();
    });
    tally();
  }

  function initWater(root) {
    var box = root.querySelector('[data-tg-water]');
    if (!box) return;
    all(box, '[data-tg-calc-controls]').forEach(function (el) {
      el.hidden = false;
    });
    var people = box.querySelector('[data-tg-people]');
    var mode = box.querySelector('[data-tg-mode]');
    var out = box.querySelector('[data-tg-out]');
    var note = box.querySelector('[data-tg-note]');
    var camping = String(box.getAttribute('data-camping'));
    function water() {
      var n = +people.textContent;
      var l = +mode.value;
      out.textContent = n * l;
      note.textContent =
        (n > 1 ? box.getAttribute('data-note-group') : '') +
        (mode.value === camping ? box.getAttribute('data-note-camping') : box.getAttribute('data-note-day'));
    }
    all(box, '.dte-tg__step button').forEach(function (b) {
      b.addEventListener('click', function () {
        people.textContent = Math.max(1, Math.min(8, +people.textContent + +b.getAttribute('data-d')));
        water();
      });
    });
    mode.addEventListener('change', water);
    water();
  }

  function init(root) {
    var cfg = {};
    try {
      cfg = JSON.parse(root.querySelector('[data-tg-config]').textContent);
    } catch (e) {
      cfg = {};
    }
    var update = cfg.mode === 'stops' ? initStops(root, cfg) : initTrail(root, cfg);
    initToggle(root, update);
    initPack(root);
    initWater(root);
  }

  all(document, '[data-tg]').forEach(init);
})();
